import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../api/httpClient";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Divider,
  IconButton,
  Container,
  Paper,
  MenuItem,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Switch,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import InputMask from "react-input-mask";
import {
  parseAddressString,
  validateAddressFields,
  formatCanonicalAddress,
  addressesAreEqual,
} from "../../../../globals/utils/addressValidation";
import AddressSubForm from "../../../../globals/components/AddressSubForm";
import {
  preferredBillingFromSelectValue,
  preferredBillingToSelectValue,
} from "./professionalGuardianBillingHelpers";
 

function ProfessionalGuardianView() {
  const { pg_id } = useParams();
  const navigate = useNavigate();
  const { role } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  // Mailing/physical addresses live as STRUCTURED objects in component state
  // so the user edits 4 separate inputs (street/city/state/zip) - the same UX
  // as the Create form. We hydrate these on load by parsing the legacy single
  // string from the backend, and we serialize them back to that shape via
  // formatCanonicalAddress() on save. This guarantees the legacy DB column
  // can never receive a 3-part / 5-part / garbage value ever again.
  const [formValues, setFormValues] = useState({
    full_name: "",
    preferred_name: "",
    business_name: "",
    mailing_address: { street_address: "", city: "", state: "", zipCode: "" },
    physical_address: { street_address: "", city: "", state: "", zipCode: "" },
    // Preserve the original raw values so we can show them when the parser
    // could not split them (so the user sees what they need to fix).
    mailing_address_raw: "",
    physical_address_raw: "",
    email_address: "",
    cell_phone: "",
    permission_to_text: false,
    date_of_birth: "",
    date_of_first_bond: "",
    oppg_number: "",
    ams_customer_number: "",
    managed_by: null,
    professional_fiduciary_questionnaire_last_completion_date: "",
    general_indemnity_agreement_on_file: false,
    letter_of_authority: false,
    letter_of_authority_file: null,
    total_bonds: 0,
    total_outstanding_bond_amount: 0,
    preferred_billing: null,
  });

  const [singleLimit, setSingleLimit] = useState(0);
  const [aggregateLimit, setAggregateLimit] = useState(0);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  // Per-field validation errors keyed by qualified name, e.g.
  // "mailing_address.street_address". Drives `error` + `helperText` props on
  // the address inputs. Populated by handleSaveChanges, cleared on edit.
  const [fieldErrors, setFieldErrors] = useState({});

  // When checked, the Physical Address sub-form is hidden and on save BOTH
  // `mailing_address` and `physical_address` columns receive the SAME
  // canonical 4-part string. Derived from the existing record on load
  // (auto-checked when the two strings already match), no DB column.
  const [addressesAreSame, setAddressesAreSame] = useState(false);

  // PG Contacts state
  const [pgContacts, setPgContacts] = useState([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    is_admin: false,
  });
  const [contactEmailError, setContactEmailError] = useState("");
  const [sendContactInvite, setSendContactInvite] = useState(false);
  const [invitingContact, setInvitingContact] = useState(null);
  const [deletingContact, setDeletingContact] = useState(null);
  const [addingContact, setAddingContact] = useState(false);
  const [updatingContact, setUpdatingContact] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  // Email existence check state
  

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };


  // Fetch PG Contacts
  const fetchPgContacts = React.useCallback(async () => {
    console.log("Fetching PG contacts for pg_id:", pg_id);
    try {
      const response = await apiClient(`/pg-contacts/pg/${pg_id}/`, "GET");
      console.log("PG contacts response:", response);
      setPgContacts(response.pg_contacts || []);
    } catch (error) {
      console.error("Failed to fetch PG contacts:", error);
    }
  }, [pg_id]);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const guardianData = await apiClient(
          `/professional-guardian-user/${pg_id}/`
        );
        setFormValues((prev) => ({
          ...prev,
          full_name: guardianData.full_name || "",
          preferred_name: guardianData.preferred_name || "",
          business_name: guardianData.business_name || "",
          // Hydrate the structured 4-field shape from the legacy single-string
          // column. parseAddressString is forgiving of 3-part / merged
          // state+zip / etc. - whatever it cannot identify is returned as ""
          // so the inline validation below shows the user what to fix.
          mailing_address: parseAddressString(guardianData.mailing_address),
          physical_address: parseAddressString(guardianData.physical_address),
          mailing_address_raw: guardianData.mailing_address || "",
          physical_address_raw: guardianData.physical_address || "",
          email_address: guardianData.user?.email || "",
          username: guardianData.user?.username || "",
          last_2fa_verified: guardianData.user?.last_2fa_verified || null,
          cell_phone: guardianData.cell_phone || "",
          permission_to_text: guardianData.permission_to_text || false,
          date_of_birth: guardianData.date_of_birth || "",
          date_of_first_bond: guardianData.date_of_first_bond || "",
          oppg_number: guardianData.oppg_number || "",
          ams_customer_number: guardianData.ams_customer_number || "",
          professional_fiduciary_questionnaire_last_completion_date:
            guardianData.professional_fiduciary_questionnaire_last_completion_date ||
            "",
          managed_by: guardianData.managed_by,
          general_indemnity_agreement_on_file:
            guardianData.general_indemnity_agreement_on_file || false,
          letter_of_authority: guardianData.letter_of_authority || false,
          letter_of_authority_file:
            guardianData.letter_of_authority_file || null,
          total_bonds: guardianData.total_bonds || 0,
          total_outstanding_bond_amount:
            guardianData.total_outstanding_bond_amount || 0,
          preferred_billing: guardianData.preferred_billing ?? null,
        }));

        // Derive the same-address toggle from the existing record so users
        // see "on" by default when the PG was already saved with both
        // columns identical (no DB column required, see plan).
        setAddressesAreSame(
          addressesAreEqual(
            guardianData.mailing_address,
            guardianData.physical_address
          )
        );

        const limits = await apiClient(
          "/guardian-limits/most-recent",
          "GET",
          {},
          { professional_guardian_id: pg_id }
        );
        setSingleLimit(limits.line_of_business_single_limit || 0);
        setAggregateLimit(limits.line_of_business_aggregate_limit || 0);

        // Fetch PG Contacts
        await fetchPgContacts();
      } catch (error) {
        console.error("Failed to fetch data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [pg_id, fetchPgContacts]);

  // Separate useEffect to ensure PG contacts are fetched
  useEffect(() => {
    if (pg_id) {
      console.log("useEffect triggered for pg_id:", pg_id);
      fetchPgContacts();
    }
  }, [pg_id, fetchPgContacts]);

  // PG Contact form handlers
  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === "email") {
      if (!value) {
        setContactEmailError("");
      } else if (!validateEmail(value)) {
        setContactEmailError("Please enter a valid email address");
      } else {
        setContactEmailError("");
      }
    }
  };

  const handleAddContact = async () => {
    if (!contactForm.full_name || !contactForm.email) {
      toast.error("Full name and email are required");
      return;
    }

    if (contactEmailError) {
      toast.error("Please fix email validation errors");
      return;
    }

    setAddingContact(true);

    try {
      const payload = sanitizePayload({
        pg: pg_id,
        full_name: contactForm.full_name,
        email: contactForm.email,
        phone_number: contactForm.phone_number,
        is_admin: contactForm.is_admin,
        send_invite: sendContactInvite,
      });
      await apiClient("/pg-contacts-invite/", "POST", payload);
      
      // Refresh contacts list
      await fetchPgContacts();
      
      // Reset form
      setContactForm({
        full_name: "",
        email: "",
        phone_number: "",
        is_admin: false,
      });
      setContactDialogOpen(false);
      setContactEmailError("");
      
      // Show success toast after modal is closed
      toast.success(
        sendContactInvite
          ? `Contact ${contactForm.full_name} added and invite sent successfully!`
          : `Contact ${contactForm.full_name} added successfully!`
      );
    } catch (error) {
      toast.error(`Failed to add contact: ${error.response?.data?.error || error.message}`);
    } finally {
      setAddingContact(false);
    }
  };

  const handleEditContact = (contact) => {
    setContactForm({
      full_name: contact.full_name,
      email: contact.email,
      phone_number: contact.phone_number || "",
      is_admin: contact.is_admin || false,
    });
    setEditingContact(contact);
    setContactDialogOpen(true);
  };

  const handleUpdateContact = async () => {
    if (!contactForm.full_name || !contactForm.email) {
      toast.error("Full name and email are required");
      return;
    }

    if (contactEmailError) {
      toast.error("Please fix email validation errors");
      return;
    }

    setUpdatingContact(true);

    try {
      const payload = sanitizePayload({
        full_name: contactForm.full_name,
        email: contactForm.email,
        phone_number: contactForm.phone_number,
        is_admin: contactForm.is_admin,
      });
      if (!editingContact?.contact_id) {
        return;
      }
      await apiClient(`/pg-contacts/${editingContact.contact_id}/`, "PUT", payload);
      
      // Refresh contacts list
      await fetchPgContacts();
      
      // Reset form
      setContactForm({
        full_name: "",
        email: "",
        phone_number: "",
        is_admin: false,
      });
      setContactDialogOpen(false);
      setEditingContact(null);
      setContactEmailError("");
      
      // Show success toast after contacts are loaded
      toast.success(`Contact ${contactForm.full_name} updated successfully!`);
    } catch (error) {
      toast.error(`Failed to update contact: ${error.response?.data?.error || error.message}`);
    } finally {
      setUpdatingContact(false);
    }
  };

  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteContact = async () => {
    if (!contactToDelete) return;

    setDeletingContact(contactToDelete.contact_id);
    try {
      await apiClient(`/pg-contacts/${contactToDelete.contact_id}/delete/`, "DELETE");
      
      // Refresh contacts list
      await fetchPgContacts();
      
      // Show success toast after contacts are loaded
      toast.success(`Contact ${contactToDelete.full_name} deleted successfully!`);
    } catch (error) {
      toast.error(`Failed to delete contact: ${error.response?.data?.error || error.message}`);
    } finally {
      setDeletingContact(null);
      setDeleteDialogOpen(false);
      setContactToDelete(null);
    }
  };

  const handleResendInvite = async (contact) => {
    setInvitingContact(contact.contact_id);
    try {
      const response = await apiClient(`/pg-contacts/${contact.contact_id}/resend-invite/`, "PATCH");
      
      toast.success(`Invite resent to ${contact.full_name}`);
      
      // Optionally show the invite link in a toast or copy to clipboard
      if (response.invite_link) {
        try {
          await navigator.clipboard.writeText(response.invite_link);
          toast.info("Invite link copied to clipboard");
        } catch (clipboardError) {
          console.warn("Failed to copy to clipboard:", clipboardError);
          // Don't show error toast for clipboard issues, just log it
        }
      }
      
      // Refresh contacts list to get updated status
      await fetchPgContacts();
    } catch (error) {
      toast.error(`Failed to resend invite to ${contact.full_name}: ${error.response?.data?.error || error.message}`);
    } finally {
      setInvitingContact(null);
    }
  };

  const openContactDialog = () => {
    setContactForm({
      full_name: "",
      email: "",
      phone_number: "",
      dob: "",
      is_admin: false,
    });
    setEditingContact(null);
    setContactEmailError("");
    setSendContactInvite(false);
    setContactDialogOpen(true);
  };

  // Clear a field-level error once the user starts editing the offending input.
  const clearFieldError = (key) => {
    if (!fieldErrors[key]) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Address sub-forms own their own state via the AddressSubForm component
    // (see render below). This handler only deals with flat top-level fields.
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    clearFieldError(name);
  };

  // When the user flips the "addresses are the same" toggle ON, mirror
  // mailing into physical and clear any physical errors so the (now hidden)
  // Physical sub-form can't block submit. When flipped OFF we keep whatever
  // physical values were last entered so the user doesn't lose work.
  const handleAddressesAreSameChange = (checked) => {
    setAddressesAreSame(checked);
    if (checked) {
      setFormValues((prev) => ({
        ...prev,
        physical_address: { ...prev.mailing_address },
      }));
      setFieldErrors((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          if (k.startsWith("physical_address.")) delete next[k];
        });
        return next;
      });
    }
  };

  const sanitizePayload = (payload) => {
    return Object.fromEntries(
      Object.entries(payload).filter(
        ([key, value]) => value !== "" && value !== null
      )
    );
  };

  const handleSaveChanges = async () => {
    // Validate the structured address sub-forms BEFORE we serialize them
    // back to the legacy single-string column. This is the guardrail that
    // stops new bad rows from ever reaching the DB. Mailing is always
    // required; Physical is required only when the toggle is OFF.
    const mailingErrors = validateAddressFields(formValues.mailing_address, {
      requireAll: true,
      fieldPrefix: "mailing_address.",
    });
    const physicalErrors = addressesAreSame
      ? {}
      : validateAddressFields(formValues.physical_address, {
          requireAll: true,
          fieldPrefix: "physical_address.",
        });
    const allAddressErrors = { ...mailingErrors, ...physicalErrors };
    if (Object.keys(allAddressErrors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...allAddressErrors }));
      const summary = Object.entries(allAddressErrors)
        .map(([key, msg]) => {
          const which = key.startsWith("mailing_address.")
            ? "Mailing"
            : "Physical";
          return `${which} Address - ${msg}`;
        })
        .join("; ");
      toast.error(`Please fix the address fields: ${summary}`);
      return;
    }

    // Build the canonical "street, city, state, zip" string for the legacy
    // column. formatCanonicalAddress returns null when invalid, but we just
    // validated above so it is guaranteed to return a string here. When the
    // toggle is ON, send the mailing canonical for both columns so they
    // stay byte-identical in the DB.
    const mailingCanonical = formatCanonicalAddress(formValues.mailing_address);
    const physicalCanonical = addressesAreSame
      ? mailingCanonical
      : formatCanonicalAddress(formValues.physical_address);

    // Strip the structured/raw address keys out of the PATCH body - they are
    // frontend-only - and replace them with the canonical strings the
    // backend serializer already understands.
    const {
      mailing_address: _ma,
      physical_address: _pa,
      mailing_address_raw: _mar,
      physical_address_raw: _par,
      ...rest
    } = formValues;
    const payload = {
      ...rest,
      mailing_address: mailingCanonical,
      physical_address: physicalCanonical,
    };

    setLoading(true);
    try {
      await apiClient(
        `/professional-guardian-user/${pg_id}/`,
        "PATCH",
        sanitizePayload(payload)
      );
      // Refresh the raw cache so future re-renders show the new canonical
      // value. Also re-derive the toggle so it stays in sync if the user
      // turned it on/off as part of this save.
      setFormValues((prev) => ({
        ...prev,
        mailing_address_raw: mailingCanonical,
        physical_address_raw: physicalCanonical,
      }));
      setAddressesAreSame(
        addressesAreEqual(mailingCanonical, physicalCanonical)
      );
      toast.success("Changes saved successfully!");
      setEditing(false); // Exit editing mode after saving
    } catch (error) {
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await apiClient(`/professional-guardian-user/${pg_id}/`, "DELETE");
      toast.success("Guardian deleted successfully!");
      navigate("/vermost-professional-guardian-list");
    } catch (error) {
      toast.error("Failed to delete guardian. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    try {
      const payload = {
        email: formValues.email_address,
      };
      await apiClient("/professional-guardian/send-invite", "POST", payload);
      toast.success("Invite sent successfully!");
    } catch (error) {
      console.error("Failed to send invite:", error);
      toast.error("Failed to send invite!");
    }
  };

  const handleSubmitLimit = async () => {
    setLoading(true);
    try {
      await apiClient("/guardian-limits/", "POST", {
        professional_guardian: pg_id,
        line_of_business_single_limit: singleLimit,
        line_of_business_aggregate_limit: aggregateLimit,
        letter_of_authority_effective_date: effectiveDate,
      });
      toast.success("Limit added successfully!");
      setOpen(false);
    } catch (error) {
      toast.error("Failed to add limit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "";
    return new Intl.NumberFormat("en-US").format(amount);
  };

  // Format currency without decimals
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  // --- Internal Users for Managed by ---
  const [internalUsers, setInternalUsers] = useState([]);

  const fetchInternalUsers = async () => {
    try {
      const internalUsers = await apiClient("/internal-user", "GET");
      setInternalUsers(internalUsers.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchInternalUsers();
  }, []);

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ marginTop: 2 }}>
        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<VisibilityIcon />}
            sx={{ marginRight: "10px" }}
            onClick={() =>
              navigate(`/professional_guardian_view_bonds/${pg_id}`)
            }
          >
            View Bonds
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/guardian-limits/${pg_id}`)}
          >
            Manage Limits
          </Button>
        </Box>
        <Paper
          sx={{ padding: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
        >
          {/* Header Section with light spacing */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Professional Guardian Information
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {formValues.full_name} • {formValues.email_address}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                ...(formValues?.last_2fa_verified ? {} : { color: "warning.main" }),
              }}
            >
              Registration Complete: {formValues?.last_2fa_verified ? "Yes" : "No"}
            </Typography>
            {formValues?.last_2fa_verified && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
              Username: {formValues?.username || "—"} • Last Logged In:{" "}
              {formatDateTime(formValues?.last_2fa_verified)}
            </Typography>
            )}
            <Divider sx={{ mb: 2 }} />

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="h6" color="text.secondary">Total Bonds</Typography>
                      <IconButton size="small" onClick={() => setEditing(!editing)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {editing ? (
                      <TextField fullWidth variant="standard" name="total_bonds" value={formValues.total_bonds} onChange={handleInputChange} sx={{ mt: 1 }} />
                    ) : (
                      <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {formatAmount(formValues.total_bonds)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="h6" color="text.secondary">Outstanding Amount</Typography>
                      <IconButton size="small" onClick={() => setEditing(!editing)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {editing ? (
                      <TextField fullWidth variant="standard" name="total_outstanding_bond_amount" value={formValues.total_outstanding_bond_amount} onChange={handleInputChange} sx={{ mt: 1 }} />
                    ) : (
                      <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {formatCurrency(formValues.total_outstanding_bond_amount)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" color="text.secondary">Single Limit</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{formatCurrency(singleLimit)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" color="text.secondary">Aggregate Limit</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{formatCurrency(aggregateLimit)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Simple section separators */}
          <Typography variant="h6" sx={{ marginBottom: 2 }}>Edit Guardian Details</Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Personal Information */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Full Name"
                name="full_name"
                fullWidth
                value={formValues.full_name || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Preferred Name"
                name="preferred_name"
                fullWidth
                value={formValues.preferred_name || ""}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Business Information */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Business Name"
                name="business_name"
                fullWidth
                value={formValues.business_name || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="OPPG Number"
                name="oppg_number"
                fullWidth
                value={formValues.oppg_number || ""}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Contact Information */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Email Address"
                name="email_address"
                fullWidth
                value={formValues.email_address || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputMask
                mask="999-999-9999"
                value={formValues.cell_phone || ""}
                onChange={handleInputChange}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    fullWidth
                    label="Cell Phone"
                    name="cell_phone"
                    placeholder="123-456-7890"
                  />
                )}
              </InputMask>
            </Grid>

            {/* Same-as toggle - when on, the Physical sub-form is hidden and
                both DB columns receive the same canonical string at submit.
                Initial state derived from the existing record on load. */}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={addressesAreSame}
                    onChange={(e) =>
                      handleAddressesAreSameChange(e.target.checked)
                    }
                  />
                }
                label="Mailing and Physical addresses are the same"
              />
            </Grid>

            {/* Mailing Address - shared 4-field sub-form (Google Places search,
                State Autocomplete, ZIP mask, blur validation). The same JSX
                used by the Create screen, so editors can never re-introduce
                malformed single-string addresses (the Kathleen-Hayes case). */}
            <Grid size={{ xs: 12 }}>
              <AddressSubForm
                label="Mailing Address"
                value={formValues.mailing_address}
                onChange={(next) => {
                  setFormValues((prev) => ({
                    ...prev,
                    mailing_address: next,
                    // Keep physical mirrored while toggle is on.
                    ...(addressesAreSame
                      ? { physical_address: { ...next } }
                      : {}),
                  }));
                }}
                errors={fieldErrors}
                setErrors={setFieldErrors}
                fieldPrefix="mailing_address."
                rawValueIfUnparsed={formValues.mailing_address_raw}
              />
            </Grid>

            {/* Physical Address (hidden when toggle is on) */}
            {!addressesAreSame && (
              <Grid size={{ xs: 12 }}>
                <AddressSubForm
                  label="Physical Address"
                  value={formValues.physical_address}
                  onChange={(next) =>
                    setFormValues((prev) => ({
                      ...prev,
                      physical_address: next,
                    }))
                  }
                  errors={fieldErrors}
                  setErrors={setFieldErrors}
                  fieldPrefix="physical_address."
                  rawValueIfUnparsed={formValues.physical_address_raw}
                />
              </Grid>
            )}

            {/* Additional Information */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                fullWidth
                value={formValues.date_of_birth || ""}
                onChange={handleInputChange}
                slotProps={{ inputLabel: { shrink: true }}}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Date of First Bond"
                name="date_of_first_bond"
                type="date"
                fullWidth
                value={formValues.date_of_first_bond || ""}
                onChange={handleInputChange}
                slotProps={{ inputLabel: { shrink: true }}}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="AMS Customer Number"
                name="ams_customer_number"
                fullWidth
                value={formValues.ams_customer_number || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Fiduciary Questionnaire Date"
                name="professional_fiduciary_questionnaire_last_completion_date"
                type="date"
                fullWidth
                value={
                  formValues.professional_fiduciary_questionnaire_last_completion_date ||
                  ""
                }
                onChange={handleInputChange}
                slotProps={{ inputLabel: { shrink: true }}}
              />
            </Grid>
            {/* Managed by */}
            {(role === "super_user" || role === "internal_user") && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Managed By"
                  name="managed_by"
                  fullWidth
                  variant="outlined"
                  value={formValues.managed_by || ""}
                  onChange={(e) =>
                    setFormValues({ ...formValues, managed_by: e.target.value })
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {internalUsers.map((user) => (
                    <MenuItem
                      key={user.internal_user_id}
                      value={user.internal_user_id}
                    >
                      {user.full_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Preferred Billing Method"
                name="preferred_billing"
                fullWidth
                required
                value={preferredBillingToSelectValue(formValues.preferred_billing)}
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    preferred_billing: preferredBillingFromSelectValue(e.target.value),
                  })
                }
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />

          {/* Document Checkboxes */}
          <Box sx={{ mt: 1, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Document Status</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={<Checkbox checked={formValues.permission_to_text || false} onChange={handleInputChange} name="permission_to_text" />}
                  label="Permission to Text"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={<Checkbox checked={formValues.general_indemnity_agreement_on_file || false} onChange={handleInputChange} name="general_indemnity_agreement_on_file" />}
                  label="GIA on File"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={<Checkbox checked={formValues.letter_of_authority || false} onChange={handleInputChange} name="letter_of_authority" />}
                  label="Letter of Authority"
                />
              </Grid>
            </Grid>
          </Box>
          <Divider sx={{ my: 3 }} />

          {/* PG Contacts Section */}
          <Box sx={{ marginTop: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Professional Guardian Contacts</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openContactDialog}>Add Contact</Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {pgContacts.length > 0 ? (
              <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
                <Table>
                <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Phone Number</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ backgroundColor: "#fff" }}>
                    {pgContacts.map((contact) => (
                      <TableRow
                        key={contact.contact_id}
                        sx={{
                          backgroundColor: "#fff",
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        <TableCell>{contact.full_name}</TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>{contact.phone_number || "-"}</TableCell>
                        <TableCell>
                          {contact.is_admin ? (
                            <Chip label="Admin" color="info" size="small" />
                          ) : (
                            <Chip label="Contact" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {contact.is_accepted ? (
                            <Chip label="Active" color="success" size="small" />
                          ) : (
                            <Chip label="Invited" color="warning" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="primary" onClick={() => handleEditContact(contact)}>
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteContact(contact)} disabled={deletingContact === contact.contact_id}>
                            {deletingContact === contact.contact_id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                          {contact.is_accepted !== true && (
                            <IconButton size="small" onClick={() => handleResendInvite(contact)} disabled={invitingContact === contact.contact_id} title="Resend Invite">
                              {invitingContact === contact.contact_id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RefreshIcon />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No contacts added yet. Click "Add Contact" to add professional guardian contacts.</Alert>
            )}
          </Box>
          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ mt: 2, textAlign: "right" }}>
            <Button variant="contained" color="error" onClick={handleDelete} sx={{ mr: 2 }}>Delete</Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleSendInvite}
              sx={{ mr: 2, backgroundColor: "#9c27b0", "&:hover": { backgroundColor: "#7b1fa2" } }}
            >
              Resend Invite
            </Button>
            <Button variant="contained" color="primary" onClick={handleSaveChanges} disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Limits Dialog (unchanged) */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Add Limit</DialogTitle>
        <DialogContent sx={{ width: "400px" }}>
          <TextField
            autoFocus
            margin="dense"
            label="Single Limit"
            type="number"
            fullWidth
            value={singleLimit}
            onChange={(e) => setSingleLimit(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Aggregate Limit"
            type="number"
            fullWidth
            value={aggregateLimit}
            onChange={(e) => setAggregateLimit(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Effective Date"
            type="date"
            fullWidth
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmitLimit}
            color="primary"
            variant="contained"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingContact ? "Edit Contact" : "Add Contact"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Full Name"
                name="full_name"
                value={contactForm.full_name}
                onChange={handleContactInputChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleContactInputChange}
                error={Boolean(contactEmailError)}
                helperText={contactEmailError || ""}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <InputMask
                mask="+1-999-999-9999"
                value={contactForm.phone_number}
                onChange={handleContactInputChange}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    fullWidth
                    label="Phone Number"
                    name="phone_number"
                    placeholder="+1-555-123-4567"
                  />
                )}
              </InputMask>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={contactForm.is_admin}
                    onChange={(e) => setContactForm(prev => ({ ...prev, is_admin: e.target.checked }))}
                  />
                }
                label="Admin Privileges"
              />
            </Grid>
            {!editingContact && (
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendContactInvite}
                      onChange={(e) => setSendContactInvite(e.target.checked)}
                    />
                  }
                  label="Send Invite Email"
                />
              </Grid>
            )}
            {editingContact && editingContact.is_accepted !== true && (
              <Grid size={{ xs: 12 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={invitingContact === editingContact.contact_id ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={() => handleResendInvite(editingContact)}
                  disabled={invitingContact === editingContact.contact_id}
                  fullWidth
                >
                  Resend Invite
                </Button>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)} disabled={addingContact || updatingContact}>Cancel</Button>
          <Button 
            onClick={editingContact ? handleUpdateContact : handleAddContact}
            variant="contained"
            disabled={!contactForm.full_name || !contactForm.email || contactEmailError || addingContact || updatingContact}
          >
            {addingContact || updatingContact ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              editingContact ? "Update" : "Add"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Delete Contact
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{contactToDelete?.full_name}</strong>? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
            disabled={deletingContact === contactToDelete?.contact_id}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteContact}
            variant="contained"
            color="error"
            disabled={deletingContact === contactToDelete?.contact_id}
          >
            {deletingContact === contactToDelete?.contact_id ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProfessionalGuardianView;
